import { useEffect, useMemo, useState } from 'react';
import { Form, Input, InputNumber, Modal, Select, message } from '@maxtropy/components';
import { Spin } from 'antd';
import {
  apiV2GasPreparationStationDeviceListPost,
  apiV2GasPreparationStationCompressorAddPost,
  apiV2GasPreparationStationCompressorDevicePost,
  apiV2GasPreparationStationCompressorEditPost,
  apiV2GasPreparationStationCompressorGetPost,
} from '@maxtropy/device-customer-apis-v2';
import { DeviceType, NodeType, OperationType } from '../../utils';
import { DeviceList, ModalProp } from '../../IntrasiteTopology';

const FormItem = Form.Item;

const CompressorNode = ({ onCancel, onOk, operationType, selectedNode, stationData, uetId }: ModalProp) => {
  const [form] = Form.useForm();
  const [electricityMeterList, setElectricityMeterList] = useState<DeviceList[]>([]);
  const [flowmeterList, setFlowmeterList] = useState<DeviceList[]>([]);
  const [pressureGaugeList, setPressureGaugeList] = useState<DeviceList[]>([]);
  const [thermometerList, setThermometerList] = useState<DeviceList[]>([]);
  const [compressorDeviceList, setCompressorDeviceList] = useState<DeviceList[]>([]);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [id, setId] = useState<number>();
  const [type, setType] = useState<number>();

  useEffect(() => {
    if (operationType === OperationType.EDIT) {
      const { selfId, type } = selectedNode.getData();
      setLoading(true);
      apiV2GasPreparationStationCompressorGetPost({ id: selfId })
        .then(res => {
          form.setFieldsValue({
            code: res?.code,
            name: res?.name,
            electricityMeterId: res?.electricityMeterId,
            flowmeterId: res?.flowmeterId,
            pressureGaugeId: res?.pressureGaugeId,
            thermometerId: res?.thermometerId,
            compressDeviceId: res?.compressDeviceId,
            power: res?.power,
            id: res?.id,
          });
          setId(res?.id);
          setType(type);
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode, operationType]);

  useEffect(() => {
    apiV2GasPreparationStationCompressorDevicePost({}).then(res => {
      setCompressorDeviceList((res?.list as DeviceList[]) ?? []);
    });
  }, []);

  useEffect(() => {
    if ((operationType === OperationType.EDIT && id) || operationType === OperationType.ADD) {
      apiV2GasPreparationStationDeviceListPost({
        nodeId: id,
        nodeType: type as NodeType,
        uetId,
        stationId: stationData?.stationId,
        stationType: stationData?.stationType,
        deviceType: [
          DeviceType.ElectricityMeter,
          DeviceType.Flowmeter,
          DeviceType.PressureGauge,
          DeviceType.Thermometer,
        ],
      }).then(res => {
        setElectricityMeterList(
          res?.list?.find(item => item.deviceType === DeviceType.ElectricityMeter)?.deviceBasicDtoList ?? []
        );
        setFlowmeterList(res?.list?.find(item => item.deviceType === DeviceType.Flowmeter)?.deviceBasicDtoList ?? []);
        setPressureGaugeList(
          res?.list?.find(item => item.deviceType === DeviceType.PressureGauge)?.deviceBasicDtoList ?? []
        );
        setThermometerList(
          res?.list?.find(item => item.deviceType === DeviceType.Thermometer)?.deviceBasicDtoList ?? []
        );
      });
    }
  }, [stationData, uetId, id, type, operationType]);

  const electricityMeterOptions = useMemo(() => {
    if (electricityMeterList?.length !== 0) {
      return electricityMeterList?.map(i => ({ label: i.name, value: i.id }));
    }
  }, [electricityMeterList]);
  const flowmeterOptions = useMemo(() => {
    if (flowmeterList?.length !== 0) {
      return flowmeterList?.map(i => ({ label: i.name, value: i.id }));
    }
  }, [flowmeterList]);

  const pressureGaugeOptions = useMemo(() => {
    if (pressureGaugeList?.length !== 0) {
      return pressureGaugeList?.map(i => ({ label: i.name, value: i.id }));
    }
  }, [pressureGaugeList]);
  const thermometerOptions = useMemo(() => {
    if (thermometerList?.length !== 0) {
      return thermometerList?.map(i => ({ label: i.name, value: i.id }));
    }
  }, [thermometerList]);

  const compressorDeviceOptions = useMemo(() => {
    return (compressorDeviceList ?? []).map(i => ({ label: i.name, value: i.id }));
  }, [compressorDeviceList]);

  const handleForm = () => {
    form.validateFields().then(() => {
      const values = {
        ...form.getFieldsValue(true),
        canvasId: selectedNode?.id,
        stationId: stationData?.stationId,
        stationCode: stationData?.stationCode,
      };

      setConfirmLoading(true);
      if (operationType === OperationType.ADD) {
        apiV2GasPreparationStationCompressorAddPost(values)
          .then(res => {
            message.success('新增成功');
            onOk({ ...values, selfId: res.id });
          })
          .finally(() => setConfirmLoading(false));
      }

      if (operationType === OperationType.EDIT) {
        apiV2GasPreparationStationCompressorEditPost(values)
          .then(res => {
            message.success('编辑成功');
            onOk({ ...values, selfId: res.id });
          })
          .finally(() => setConfirmLoading(false));
      }
    });
  };

  return (
    <Modal
      title="空压机节点"
      contentClassName="modal-form-content"
      open
      bodyScroll
      onCancel={onCancel}
      onOk={handleForm}
      confirmLoading={confirmLoading}
    >
      <Spin spinning={loading}>
        <Form form={form}>
          {operationType === OperationType.EDIT && (
            <FormItem label="节点编号" name="code">
              <Input placeholder="请输入" disabled />
            </FormItem>
          )}
          <FormItem
            label="节点名称"
            name="name"
            rules={[
              { required: true, message: '请输入节点名称' },
              { min: 2, message: '最少输入2个字符' },
              { max: 50, message: '最多输入50个字符' },
            ]}
          >
            <Input placeholder="请输入节点名称" allowClear />
          </FormItem>
          <FormItem label="计量电表" name="electricityMeterId">
            <Select
              placeholder="请选择"
              options={electricityMeterOptions}
              showSearch
              allowClear
              optionFilterProp="label"
            />
          </FormItem>
          <FormItem label="流量计" name="flowmeterId">
            <Select placeholder="请选择" options={flowmeterOptions} showSearch allowClear optionFilterProp="label" />
          </FormItem>
          <FormItem label="压力表" name="pressureGaugeId">
            <Select
              placeholder="请选择"
              options={pressureGaugeOptions}
              showSearch
              allowClear
              optionFilterProp="label"
            />
          </FormItem>
          <FormItem label="温度计" name="thermometerId">
            <Select placeholder="请选择" options={thermometerOptions} showSearch allowClear optionFilterProp="label" />
          </FormItem>
          <FormItem label="关联空压机" name="compressDeviceId">
            <Select
              placeholder="请选择"
              options={compressorDeviceOptions}
              showSearch
              allowClear
              optionFilterProp="label"
            />
          </FormItem>
          <FormItem label="额定功率" name="power">
            <InputNumber
              placeholder="请输入"
              min={0}
              precision={2}
              max={99999999.99}
              addonAfter="kW"
              style={{ width: '100%' }}
            />
          </FormItem>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CompressorNode;
